import { useEffect } from "react";
import { create } from "zustand";

import type {
  AskUserQuestionMultipleChoice,
  AskUserQuestionOneOfTwo,
  AskUserQuestionPhotoUpload,
  AskUserQuestionScale,
  AskUserQuestionSingleChoice,
  AskUserQuestionYesOrNo,
  AskUserQuestionsContent,
} from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import type { AttachmentType } from "@/api/mutations/conversation";
import { captureException } from "@/sentry";
import { AssistantMessageTypes } from "@/types";

type AskUserQuestionsContentQuestionsItem =
  | AskUserQuestionMultipleChoice
  | AskUserQuestionSingleChoice
  | AskUserQuestionOneOfTwo
  | AskUserQuestionYesOrNo
  | AskUserQuestionScale
  | AskUserQuestionPhotoUpload;

export type ChoiceAnswer = {
  type: "choice";
  selected: string[];
  otherText: string;
};
export type ScaleAnswer = { type: "scale"; value: string };
export type PhotoAnswer = { type: "photo"; attachmentIds: string[] };
export type AskUserAnswer = ChoiceAnswer | ScaleAnswer | PhotoAnswer;

export function emptyAnswer(
  question: AskUserQuestionsContentQuestionsItem,
): AskUserAnswer {
  switch (question.question_type) {
    case "photo_upload":
      return { type: "photo", attachmentIds: [] };
    case "scale":
      return { type: "scale", value: "" };
    default:
      return { type: "choice", selected: [], otherText: "" };
  }
}

export function isChoiceQuestionMissingOptions(
  question: AskUserQuestionsContentQuestionsItem,
): boolean {
  if (
    question.question_type !== "multiple_choice" &&
    question.question_type !== "single_choice" &&
    question.question_type !== "one_of_two"
  ) {
    return false;
  }
  const options = question.options ?? [];
  return options.length === 0;
}

type Session = {
  questions: AskUserQuestionsContentQuestionsItem[];
  answers: Record<number, AskUserAnswer>;
  attachments: Record<number, AttachmentType[]>;
  currentIndex: number;
  messageId: string;
};

type AskUserQuestionsStore = {
  session: Session | null;
  startSession: (
    questions: AskUserQuestionsContentQuestionsItem[],
    messageId: string,
  ) => void;
  setAnswer: (index: number, answer: AskUserAnswer) => void;
  goToQuestion: (index: number) => void;
  nextQuestion: () => void;
  prevQuestion: () => void;
  skipQuestion: (index: number) => void;
  setAttachments: (
    index: number,
    attachments:
      | AttachmentType[]
      | ((prev: AttachmentType[]) => AttachmentType[]),
  ) => void;
  clearSession: () => void;
};

const clampIndex = (index: number, length: number) =>
  Math.max(0, Math.min(index, length - 1));

export const useAskUserQuestionsStore = create<AskUserQuestionsStore>(
  (set) => ({
    session: null,
    startSession: (questions, messageId) =>
      set((state) => {
        if (state.session?.messageId === messageId) return state;
        const validQuestions = questions.filter(
          (q) => !isChoiceQuestionMissingOptions(q),
        );
        if (validQuestions.length !== questions.length) {
          captureException(
            "AskUserQuestions: omitting question(s) that came back with no options",
            { extra: { messageId } },
          );
        }
        if (validQuestions.length === 0) return state;
        return {
          session: {
            questions: validQuestions,
            answers: {},
            attachments: {},
            currentIndex: 0,
            messageId,
          },
        };
      }),
    setAnswer: (index, answer) =>
      set((state) => {
        if (!state.session) return state;
        return {
          session: {
            ...state.session,
            answers: { ...state.session.answers, [index]: answer },
          },
        };
      }),
    goToQuestion: (index) =>
      set((state) => {
        if (!state.session) return state;
        return {
          session: {
            ...state.session,
            currentIndex: clampIndex(index, state.session.questions.length),
          },
        };
      }),
    nextQuestion: () =>
      set((state) => {
        if (!state.session) return state;
        return {
          session: {
            ...state.session,
            currentIndex: clampIndex(
              state.session.currentIndex + 1,
              state.session.questions.length,
            ),
          },
        };
      }),
    prevQuestion: () =>
      set((state) => {
        if (!state.session) return state;
        return {
          session: {
            ...state.session,
            currentIndex: clampIndex(
              state.session.currentIndex - 1,
              state.session.questions.length,
            ),
          },
        };
      }),
    skipQuestion: (index) =>
      set((state) => {
        if (!state.session) return state;
        const { questions, attachments } = state.session;
        const question = questions[index];
        const nextAttachments =
          question?.question_type === "photo_upload"
            ? { ...attachments, [index]: [] }
            : attachments;
        const isLast = index === questions.length - 1;
        return {
          session: {
            ...state.session,
            answers: {
              ...state.session.answers,
              [index]: emptyAnswer(question),
            },
            attachments: nextAttachments,
            currentIndex: isLast
              ? state.session.currentIndex
              : clampIndex(index + 1, questions.length),
          },
        };
      }),
    setAttachments: (index, valueOrUpdater) =>
      set((state) => {
        if (!state.session) return state;
        const prev = state.session.attachments[index] ?? [];
        const next =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        return {
          session: {
            ...state.session,
            attachments: { ...state.session.attachments, [index]: next },
          },
        };
      }),
    clearSession: () => set({ session: null }),
  }),
);

export function useAskUserQuestionsSession(allMessages: ClientEventModel[]) {
  const startSession = useAskUserQuestionsStore((s) => s.startSession);
  const clearSession = useAskUserQuestionsStore((s) => s.clearSession);

  const lastMessage = allMessages.at(-1);
  const lastMessageId = lastMessage?.id;
  const questions =
    lastMessage?.type === AssistantMessageTypes.ASK_USER_QUESTIONS
      ? ((lastMessage.content as AskUserQuestionsContent)?.questions ?? [])
      : [];
  const isQuestions = questions.length > 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: `questions` is read at call time by design
  useEffect(() => {
    if (isQuestions && lastMessageId) {
      startSession(questions, lastMessageId);
    }
    return () => clearSession();
  }, [lastMessageId, isQuestions, startSession, clearSession]);
}

export type AskUserQuestionAnswer = {
  question: string;
  answer: string;
  attachment_ids?: string[];
};

export function buildQuestionAnswer(
  q: AskUserQuestionsContentQuestionsItem,
  answer: AskUserAnswer | undefined,
): AskUserQuestionAnswer {
  if (q.question_type === "photo_upload") {
    const attachmentIds = answer?.type === "photo" ? answer.attachmentIds : [];
    return {
      question: q.question,
      answer: attachmentIds.length ? "" : "Skipped",
      attachment_ids: attachmentIds,
    };
  }
  if (q.question_type === "scale") {
    const value = answer?.type === "scale" ? answer.value : "";
    return {
      question: q.question,
      answer: value
        ? `${value} (scale ${q.scale_range.min}-${q.scale_range.max})`
        : "Skipped",
    };
  }
  const choice =
    answer?.type === "choice" ? answer : { selected: [], otherText: "" };
  const parts = [...choice.selected];
  if (choice.otherText) parts.push(choice.otherText);
  return {
    question: q.question,
    answer: parts.length ? parts.join(", ") : "Skipped",
  };
}

export function collectAttachments(
  questions: AskUserQuestionsContentQuestionsItem[],
  answers: Record<number, AskUserAnswer>,
  sessionAttachments: Record<number, AttachmentType[]>,
): { attachmentIds: string[]; attachments: AttachmentType[] } {
  const attachmentIds = questions.flatMap((_q, i) => {
    const answer = answers[i];
    return answer?.type === "photo" ? answer.attachmentIds : [];
  });
  return {
    attachmentIds,
    attachments: Object.values(sessionAttachments).flat(),
  };
}

export function getStructuredPartialAnswersAndClear(
  freetextAnswer?: string | object[],
): {
  answers: AskUserQuestionAnswer[];
  attachmentIds: string[];
  attachments: AttachmentType[];
} | null {
  if (typeof freetextAnswer !== "string") return null;
  const state = useAskUserQuestionsStore.getState();
  if (!state.session) return null;

  const {
    questions,
    answers,
    attachments: sessionAttachments,
    currentIndex,
  } = state.session;
  const questionAnswers = questions
    .slice(0, currentIndex)
    .map((q, i) => buildQuestionAnswer(q, answers[i]));

  const currentQuestion = questions[currentIndex];
  if (freetextAnswer && currentQuestion) {
    questionAnswers.push({
      question: currentQuestion.question,
      answer: freetextAnswer.trim(),
    });
  }

  if (questionAnswers.length === 0) return null;

  const attachmentIds = questions.slice(0, currentIndex).flatMap((_q, i) => {
    const answer = answers[i];
    return answer?.type === "photo" ? answer.attachmentIds : [];
  });
  const attachments = Object.values(sessionAttachments).flat();

  state.clearSession();
  return { answers: questionAnswers, attachmentIds, attachments };
}
